import { Link as LinkIcon, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

interface ProductImportFromUrlCardProps {
  title?: string
  description?: string
  placeholder?: string
  submitLabel?: string
  pendingLabel?: string
  density?: 'default' | 'compact'
  url: string
  onUrlChange: (value: string) => void
  onSubmit: () => void
  isPending?: boolean
  isError?: boolean
  errorText?: string
  disabled?: boolean
}

export function ProductImportFromUrlCard({
  title = 'Import from URL',
  description = "Enter your product page URL and we'll automatically extract the product information.",
  placeholder = 'https://your-store.com/products/your-product',
  submitLabel = 'Import Product',
  pendingLabel = submitLabel,
  density = 'default',
  url,
  onUrlChange,
  onSubmit,
  isPending = false,
  isError = false,
  errorText = 'Failed to parse URL. Please try again.',
  disabled = false,
}: ProductImportFromUrlCardProps) {
  const isCompact = density === 'compact'

  return (
    <div className={`bg-white rounded-xl border border-gray-200 ${isCompact ? 'p-4' : 'p-6'}`}>
      <h2 className={`${isCompact ? 'text-base mb-2' : 'text-lg mb-4'} font-semibold`}>{title}</h2>
      <p className={`text-gray-500 ${isCompact ? 'mb-4 text-sm' : 'mb-6'}`}>{description}</p>
      <form
        onSubmit={(event) => {
          event.preventDefault()
          onSubmit()
        }}
        className={isCompact ? 'space-y-3' : 'space-y-4'}
      >
        <div className="relative">
          <LinkIcon
            className={`absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 ${isCompact ? 'w-4 h-4' : 'w-5 h-5'}`}
          />
          <Input
            type="url"
            placeholder={placeholder}
            value={url}
            onChange={(event) => onUrlChange(event.target.value)}
            className={isCompact ? 'pl-9' : 'pl-10'}
            disabled={disabled || isPending}
          />
        </div>
        {isError && <p className="text-red-500 text-sm">{errorText}</p>}
        <Button
          type="submit"
          size={isCompact ? 'sm' : 'default'}
          disabled={!url.trim() || disabled || isPending}
        >
          {isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {isPending ? pendingLabel : submitLabel}
        </Button>
      </form>
    </div>
  )
}
